import CloseIcon from '@mui/icons-material/Close'
import NotificationsIcon from '@mui/icons-material/Notifications'
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material'
import React from 'react'

import Database from '../../types/Database'
import { generateColorFromString } from '../../utils/FormatHelpers'
import { renderMarkdown } from '../../utils/simpleMarkdown'

interface NotificationModalProps {
  open: boolean
  notifications: Database.OfficeNotification[]
  offices: Database.Office[]
  onClose: () => void
}

/**
 * NotificationModal - Modal for displaying day notifications
 *
 * Shows notification content with markdown formatting support.
 */
export default function NotificationModal({
  open,
  notifications,
  offices,
  onClose
}: NotificationModalProps) {
  if (notifications.length === 0) {
    return null
  }

  // Helper to get office name and color from officeId
  const getOfficeInfo = (officeId: string) => {
    const office = offices.find(o => o.oid === officeId)
    const name = office?.name || 'Unknown Office'
    const color = generateColorFromString(name)
    return { name, color }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      onClick={e => e.stopPropagation()}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsIcon color='primary' />
          <Typography variant='h6' component='span'>
            Day Notes
          </Typography>
        </Box>
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            color: theme => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {notifications.map((notification, index) => {
          const officeInfo = getOfficeInfo(notification.officeId)
          return (
            <Box
              key={index}
              sx={{
                mb: notifications.length > 1 ? 2 : 0,
                pb:
                  notifications.length > 1 && index < notifications.length - 1
                    ? 2
                    : 0,
                borderBottom:
                  notifications.length > 1 && index < notifications.length - 1
                    ? '1px solid'
                    : 'none',
                borderColor: 'divider'
              }}
            >
              <Box sx={{ mb: 1 }}>
                <Chip
                  label={officeInfo.name}
                  size='small'
                  sx={{
                    fontWeight: 500,
                    backgroundColor: 'transparent',
                    border: '1px solid',
                    borderColor: officeInfo.color,
                    color: 'text.primary',
                    '& .MuiChip-label': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&::before': {
                        content: '""',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: officeInfo.color,
                        display: 'inline-block'
                      }
                    }
                  }}
                />
              </Box>
              {renderMarkdown(notification.text)}
            </Box>
          )
        })}
      </DialogContent>
    </Dialog>
  )
}
